import { ConditionGroup } from "@/types/EventRule";
import { DataType } from "@/types/enums/DataType";
import { Field } from "@/types/Field";
import {
  EXPR_FILTER_KEY,
  expressionFieldKeys,
  readExprNode,
} from "./expression-ast";

// `matches_new` is a Worker-side operator (2026-05-28) used by the
// register dedup gate. It cross-references the registering guest's
// value for the same key, so there's no value input — same UI shape
// as `is_empty` / `is_not_empty`. Java's ConditionEvaluator ignores
// it; only website-builder-backend evaluates this operator.
export const noValueOperators = ["is_empty", "is_not_empty", "matches_new"];

/** A filter value carrying no real query: nullish, empty string, or empty array
 *  (the multi-pick `in` / `not_in` operators store an array — an empty one is as
 *  incomplete as a blank text value and must not be saved). */
export const isEmptyFilterValue = (v: unknown): boolean =>
  v === null ||
  v === undefined ||
  v === "" ||
  (Array.isArray(v) && v.length === 0);

/** Data types that are complex/display-only and cannot be used as condition filters */
const EXCLUDED_CONDITION_TYPES = new Set([
  DataType.OBJECT_ID,
  DataType.ID,
  DataType.CHECK_IN_OUT,
  DataType.COMPANIONS,
  DataType.ATTACHMENTS,
  DataType.SLOTTING,
  DataType.SEATING,
]);

/**
 * Activity-rollup fields are now denormalized ON the guest document, and the
 * condition surfaces carry them as RAW values for evaluation (backend
 * `GuestDTOPayloadAdapter.toConditionPayload` / `convertGuestToMap` /
 * `buildGuestDataForRules`; consumer `rawActivityRollups`), with the backend
 * allowlists updated to accept them — so they are valid condition fields and
 * are NO LONGER excluded.
 *
 * `wasWaitlisted` stays excluded: there is no `FIELDS_GUEST` def for it and no
 * producer carries it, so it can't render a working condition row.
 */
const EXCLUDED_CONDITION_FIELD_NAMES = new Set<string>([
  "wasWaitlisted",
  // Derived at read time from username+code (see default-returner's entry and
  // main-backend `GuestLoginToken`). No stored field means no query: the
  // backend subtracts it from its own filter-key allowlist, so a condition row
  // built on it would 400 — or, worse in the workflow evaluators, quietly
  // match nobody.
  "loginToken",
]);

/** Filter out fields that cannot be queried as conditions */
export function filterConditionFields(fields: Field[]): Field[] {
  return fields.filter(
    (f) =>
      !EXCLUDED_CONDITION_TYPES.has(f.dataType as DataType) &&
      !EXCLUDED_CONDITION_FIELD_NAMES.has(f.name),
  );
}

export const ADDITIONAL_FIELDS_PREFIX = "additionalFields.";

export function getFieldKey(field: Field): string {
  return field.additionalField ? `${ADDITIONAL_FIELDS_PREFIX}${field.name}` : field.name;
}

export function findFieldByKey(fields: Field[], key: string): Field | undefined {
  // Exact wire-key match first; then resolve BARE additional-field names.
  // Producers of bare custom keys are legitimate (dashboard click-through URLs,
  // goals-page/AI filter handoffs, legacy flat saved filters — the flat model
  // stores bare names), so a bare key must not read as a deleted field. The
  // fallback is restricted to additionalField defs: built-ins already match
  // exactly (their wire key IS the bare name), and a dangling
  // `additionalFields.<name>` must never resolve to a same-named built-in.
  // Name collisions between the two are blocked at field creation.
  return (
    fields.find((f) => getFieldKey(f) === key) ??
    fields.find((f) => f.additionalField && f.name === bareFieldName(key))
  );
}

/** The canonical wire key for a condition key: the resolved field's
 *  `getFieldKey` form (dotted for additional fields), or the key unchanged
 *  when it resolves to nothing. Use at EMIT sites that write keys into new
 *  payloads (dedupe rules, contact queries, conditions merges) — several
 *  backend validators reject bare custom keys, so a fallback-resolved bare
 *  key must not propagate. */
export function canonicalFieldKey(fields: Field[], key: string): string {
  const field = findFieldByKey(fields, key);
  return field ? getFieldKey(field) : key;
}

/** Rewrite every leaf key of a ConditionGroup to its canonical wire form
 *  (see canonicalFieldKey). Unresolvable and empty keys pass through
 *  unchanged, so this is safe on trees that mix in deleted-field rows. */
export function canonicalizeConditionKeys(
  group: ConditionGroup,
  fields: Field[],
): ConditionGroup {
  return {
    operator: group.operator,
    filters: (group.filters ?? []).map((f) =>
      f.key ? { ...f, key: canonicalFieldKey(fields, f.key) } : f,
    ),
    groups: (group.groups ?? []).map((g) =>
      canonicalizeConditionKeys(g, fields),
    ),
  };
}

/**
 * The bare (prefix-stripped) name of a condition key, for display of a DANGLING
 * reference. A saved condition/filter key is stored as `additionalFields.<name>`
 * (or a bare built-in name); when its field was DELETED it no longer resolves to
 * any Field, so instead of leaking the raw `additionalFields.<name>` wire key
 * (BD-5 J2) we surface just the name under a "deleted field" marker.
 */
export function bareFieldName(key: string): string {
  return key.startsWith(ADDITIONAL_FIELDS_PREFIX)
    ? key.slice(ADDITIONAL_FIELDS_PREFIX.length)
    : key;
}

/** A picked key that no longer resolves to any known field = a broken/dangling
 *  reference (its field was deleted). Empty keys (untouched rows) are not broken. */
export function isBrokenFieldKey(fields: Field[], key: string): boolean {
  return !!key && !findFieldByKey(fields, key);
}

export function cleanConditionGroup(group: ConditionGroup): ConditionGroup {
  // `filters` / `groups` may be undefined on a persisted or AI-generated group:
  // JSON-B drops empty/null collections, so a backend-loaded ConditionGroup can
  // arrive with either array missing. Default both — these helpers run on every
  // device/attachment/rule/import save path, and a bare `.filter`/`.map` here
  // would crash the editor the same way the renderer did.
  return {
    operator: group.operator,
    // A `null` entry (stored or imported data) is dropped like an empty one.
    filters: (group.filters ?? []).filter((f) => !!f && f.key !== ""),
    groups: (group.groups ?? [])
      .filter((g): g is ConditionGroup => !!g)
      .map(cleanConditionGroup)
      .filter((g) => (g.filters?.length ?? 0) > 0 || (g.groups?.length ?? 0) > 0),
  };
}

/**
 * {@link cleanConditionGroup} plus: drops rows where a field IS picked but the
 * value is still missing. For hosts that edit a tree INLINE (no popup with its
 * own validate-on-save), a gate the user disabled or navigated away from can
 * otherwise carry a half-written row into the payload — `cleanConditionGroup`
 * only strips key-less rows, so `firstName = ""` would be persisted and then
 * block every later save of the whole form. Reachable gates are still blocked
 * on submit; this only sanitizes what the user can no longer see.
 */
export function sanitizeConditionGroup(group: ConditionGroup): ConditionGroup {
  const finished = (group.filters ?? []).filter(
    (f) =>
      !!f.key &&
      (noValueOperators.includes(f.operator) || !isEmptyFilterValue(f.value)),
  );
  return {
    operator: group.operator,
    filters: finished,
    groups: (group.groups ?? [])
      .map(sanitizeConditionGroup)
      .filter((g) => (g.filters?.length ?? 0) > 0 || (g.groups?.length ?? 0) > 0),
  };
}

export function hasNonEmptyConditions(group?: ConditionGroup): boolean {
  // Tolerate a malformed value (e.g. a stray legacy array) rather than throwing
  // — these helpers read values that cross the API boundary.
  if (!group || typeof group !== "object" || Array.isArray(group)) return false;
  return (group.filters?.length ?? 0) > 0 || (group.groups?.length ?? 0) > 0;
}

/** Fresh empty AND-group — use for state init (never share a mutable literal). */
export function emptyConditionGroup(): ConditionGroup {
  return { operator: "AND", filters: [], groups: [] };
}

/** Total leaf-filter count across the tree (for "N conditions applied" badges). */
export function countConditionFilters(group?: ConditionGroup): number {
  if (!group || typeof group !== "object" || Array.isArray(group)) return 0;
  return (
    (group.filters?.length ?? 0) +
    (group.groups ?? []).reduce((sum, g) => sum + countConditionFilters(g), 0)
  );
}

export function hasIncompleteConditions(group: ConditionGroup): boolean {
  for (const f of group.filters ?? []) {
    if (!f.key) return true;
    if (!noValueOperators.includes(f.operator) && isEmptyFilterValue(f.value))
      return true;
  }
  return (group.groups ?? []).some((g) => hasIncompleteConditions(g));
}

/**
 * Like {@link hasIncompleteConditions} but IGNORES fully-untouched rows (no
 * field picked). Those are stripped on save (`cleanConditionGroup`), so they
 * aren't really "incomplete" — only a row where a field IS chosen but its
 * value is still missing should block the editor's Save / show the nag. This
 * lets the editor open with a ready-to-fill empty row without immediately
 * yelling at the user.
 */
export function hasUnfinishedConditions(group: ConditionGroup): boolean {
  for (const f of group.filters ?? []) {
    if (!f.key) continue; // untouched row → stripped on save, not "incomplete"
    // A half-built expression strip has no tree yet. Unlike an untouched row it
    // is NOT stripped on save — the sentinel key survives `cleanConditionGroup`
    // — so it would reach the wire as a filter that resolves to nothing and
    // silently governs nobody.
    if (f.key === EXPR_FILTER_KEY && readExprNode(f.expr) === null) return true;
    if (!noValueOperators.includes(f.operator) && isEmptyFilterValue(f.value))
      return true;
  }
  return (group.groups ?? []).some((g) => hasUnfinishedConditions(g));
}

/**
 * Shared 3-step validation for condition-gated actions (device modals:
 * PCreateDevice / DeviceWizard / PEditDevice / PBulkEditDevices):
 *   1. reject incomplete condition rows,
 *   2. strip empty rows/groups via cleanConditionGroup,
 *   3. require a conditionMessage when (cleaned) conditions remain.
 * Returns the cleaned actions on success, or the `errors`-namespace key of
 * the validation failure.
 */
export function validateConditionActions<
  T extends { conditions?: ConditionGroup; conditionMessage?: string },
>(
  actions: T[],
):
  | { ok: true; actions: T[] }
  | { ok: false; errorKey: "incompleteConditions" | "conditionMessageRequired" } {
  if (actions.some((a) => a.conditions && hasIncompleteConditions(a.conditions))) {
    return { ok: false, errorKey: "incompleteConditions" };
  }
  const cleaned = actions.map((a) =>
    a.conditions ? { ...a, conditions: cleanConditionGroup(a.conditions) } : a,
  );
  if (
    cleaned.some(
      (a) => hasNonEmptyConditions(a.conditions) && !a.conditionMessage?.trim(),
    )
  ) {
    return { ok: false, errorKey: "conditionMessageRequired" };
  }
  return { ok: true, actions: cleaned };
}

/** Every field key referenced by any leaf filter in the tree (deep). */
export function conditionReferencedKeys(group?: ConditionGroup): Set<string> {
  const keys = new Set<string>();
  const walk = (g?: ConditionGroup) => {
    if (!g || typeof g !== "object" || Array.isArray(g)) return;
    for (const f of g.filters ?? []) if (f.key) keys.add(f.key);
    for (const sub of g.groups ?? []) walk(sub);
  };
  walk(group);
  return keys;
}

/**
 * Field-condition cycle guard. A conditional additional field reads OTHER
 * fields' values; a field whose condition (visibility / editable / per-option)
 * references its own key — or two fields that reference each other — produce an
 * unstable / contradictory show-hide loop. Returns the set of field keys that
 * participate in a self-reference or a dependency cycle, so the editor can
 * reject the save and the renderer can fail safe (treat as always-visible).
 *
 * `conditionsOf` yields every ConditionGroup attached to a field (collapse
 * visibility + editable + optionConditions). Edge: field key → keys it reads.
 */
export function findConditionCycles(
  fields: Field[],
  conditionsOf: (f: Field) => (ConditionGroup | undefined)[],
): Set<string> {
  const keyOf = new Map<Field, string>(fields.map((f) => [f, getFieldKey(f)]));
  // Adjacency: field key → keys its conditions read (restricted to known fields).
  const known = new Set(keyOf.values());
  const deps = new Map<string, Set<string>>();
  for (const f of fields) {
    const self = keyOf.get(f)!;
    const out = deps.get(self) ?? new Set<string>();
    for (const g of conditionsOf(f)) {
      for (const k of conditionReferencedKeys(g)) {
        if (known.has(k)) out.add(k);
      }
    }
    deps.set(self, out);
  }
  // DFS cycle detection (incl. self-edges).
  const bad = new Set<string>();
  const state = new Map<string, 0 | 1 | 2>(); // 0=unseen 1=in-stack 2=done
  const stack: string[] = [];
  const dfs = (node: string) => {
    state.set(node, 1);
    stack.push(node);
    for (const next of deps.get(node) ?? []) {
      if (next === node) {
        bad.add(node); // self-reference
        continue;
      }
      const s = state.get(next) ?? 0;
      if (s === 1) {
        // back-edge: every node currently on the stack from `next` is in a cycle
        const from = stack.indexOf(next);
        for (let i = from; i < stack.length; i++) bad.add(stack[i]);
      } else if (s === 0) {
        dfs(next);
      }
    }
    stack.pop();
    state.set(node, 2);
  };
  for (const k of deps.keys()) if ((state.get(k) ?? 0) === 0) dfs(k);
  return bad;
}

/**
 * `allowExpr` must be passed by the ONE host that offers an arithmetic operand
 * (the conditional companion allowance). Everywhere else the sentinel key
 * stays what it has always been here — a key that resolves to no field, i.e.
 * an invalid row — which is also what main-backend answers for those surfaces.
 * Without this flag the check would have got WEAKER for every other caller:
 * a well-formed `expr` would have been waved through by a surface whose
 * evaluator cannot read it.
 */
export function hasInvalidConditions(
  group: ConditionGroup,
  fields: Field[],
  opts?: { allowExpr?: boolean },
): boolean {
  for (const f of group.filters ?? []) {
    if (!f.key) continue; // Empty rows are stripped by clean
    // An expression operand replaces the key, so the dangling-reference check
    // moves to the fields the EXPRESSION reads — every one of them, not just
    // the first.
    if (opts?.allowExpr && f.key === EXPR_FILTER_KEY) {
      const node = readExprNode(f.expr);
      if (node === null) return true;
      if (expressionFieldKeys(node).some((k: string) => !findFieldByKey(fields, k))) return true;
      if (noValueOperators.includes(f.operator)) return true;
      // The right-hand side of an expression comparison is a NUMBER, and the
      // Worker refuses anything else (`parseStrictNumber`) by answering false
      // for every guest. A rule that matches nobody must not save quietly, so
      // "3 Plätze" and a German "3,5" are refused here rather than shipped.
      if (!isStrictNumericValue(f.value)) return true;
      continue;
    }
    const field = findFieldByKey(fields, f.key);
    if (!field) return true; // Unknown field
    if (!noValueOperators.includes(f.operator) && isEmptyFilterValue(f.value))
      return true;
  }
  return (group.groups ?? []).some((g) => hasInvalidConditions(g, fields, opts));
}

/** Mirrors the Worker's `parseStrictNumber`: dot-decimal, whole string. */
function isStrictNumericValue(value: string | string[] | undefined): boolean {
  if (typeof value !== "string") return false;
  return /^-?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/.test(value.trim()) && value.trim() !== "";
}
